import { faPlus, faPenToSquare, faMultiply, faTriangleExclamation, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Dropdown, Offcanvas, Modal } from "react-bootstrap";
import { objectEqual } from "../../common/functions/objectComparison";
import { BackupData, CreateClusterBackup } from "../../model/CarbonExternalModels";
import { initCreateClusterBackup } from "../../store/constellations/constellationsUtils";
import ClustersDropdownSelector from "../clusters/cluster/clustersDropdown/ClustersDropdownSelector";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { apiCreateBackup } from "../../messages/api/gcmAPICallers";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import { Button } from "userful-chronos-common-ui/dist";

interface IProps {
    type: "PRIMARY" | "SECONDARY" | "DROPDOWN";
    backups: BackupData[];
}

export default function CreateBackupAndRestore(props: IProps) {
    // const dispatch = useCarbonDispatch();

    const [showForm, setShowForm] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [registerChange, setRegisterChange] = useState<boolean>(false);

    const [originalBackup, setOriginalBackup] = useState<CreateClusterBackup>(initCreateClusterBackup());
    const [currentBackup, setCurrentBackup] = useState<CreateClusterBackup>(originalBackup);

    const selectAll =
        currentBackup.supports.supportsAccount &&
        currentBackup.supports.supportsApp &&
        currentBackup.supports.supportsMedia;
    const atLeastOneSelected =
        currentBackup.supports.supportsAccount ||
        currentBackup.supports.supportsApp ||
        currentBackup.supports.supportsMedia;
    const atLeastOneClusterSelected = currentBackup.id && currentBackup.id.value !== "NOT_SET";

    const foundChanges = !objectEqual(originalBackup, currentBackup);
    const typeError = !atLeastOneSelected ? "Please make sure to select at least one backup type." : "";
    const systemError = !atLeastOneSelected ? "Please make sure to select at least one cluster." : "";
    const clusterError = !atLeastOneClusterSelected ? "Please make sure to select at least one cluster." : "";
    let nameError = currentBackup.name.length === 0 ? "Please make sure the name is unique and is not empty." : "";

    if (props.backups.find((b) => b.metadata?.name === currentBackup?.name))
        nameError = "Please make sure the name is unique and is not empty.";
    else if (props.backups.find((b) => b.id.value === currentBackup?.name))
        nameError = "Please make sure the name is unique and is not empty.";

    const permissions: GcmUserPermission = getGlobalStates().gcmUserPermission;

    let canCreate = !typeError && !nameError && !systemError && !clusterError;

    function handleClusterClick(clusterID: StringID) {
        setRegisterChange(true);
        setCurrentBackup({ ...currentBackup, id: clusterID });
    }
    function handleNameChange(event) {
        setRegisterChange(true);
        setCurrentBackup({ ...currentBackup, name: event.target.value });
    }

    const onFormShow = () => {
        setShowForm(true);
    };
    const onFormHide = () => {
        if (foundChanges) {
            setShowConfirmation(true);
        } else {
            onRestore();
            setShowForm(false);
        }
    };
    const onRestore = () => {
        setRegisterChange(false);
        setCurrentBackup(initCreateClusterBackup());
    };

    const onCreate = () => {
        apiCreateBackup(getGlobalStates().keycloak.token, currentBackup);

        onRestore();
        setShowForm(false);
    };

    if (!permissions.isClusterSetup) return null;

    return (
        <>
            {props.type === "PRIMARY" && (
                <button className="createButton" onClick={(e) => onFormShow()}>
                    <FontAwesomeIcon icon={faPlus} />
                    Create New Backup
                </button>
            )}
            {props.type === "SECONDARY" && (
                <button className="secondaryButton" onClick={(e) => onFormShow()}>
                    Create New Backup
                </button>
            )}
            {props.type === "DROPDOWN" && (
                <Dropdown.Item onClick={(e) => onFormShow()}>
                    <div className="gcm-icon-dropdown-menu-icon-container">
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </div>
                    <p>Edit</p>
                </Dropdown.Item>
            )}

            <Offcanvas show={showForm} onHide={onFormHide} placement="end">
                <div className="gcm-form-Body">
                    <div className="gcm-form-Header">
                        <div className="gcm-form-Row">
                            <h1 className="gcm-form-HeaderTitle">{false ? "Edit a Backup" : "Create a Backup"}</h1>
                            <button className="userfulIconButton" onClick={onFormHide}>
                                <FontAwesomeIcon icon={faMultiply} />
                            </button>
                        </div>
                        <div className="gcm-form-Row">
                            <h3 className="gcm-form-HeaderDescription">
                                Create a backup and name it descriptively, specifying the contents. The backup will be
                                created with current timestamp.
                            </h3>
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">Backup Details</h4>
                            <h5 className="gcm-form-ContentsDescription">
                                Name and select what should be included in this backup.
                            </h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Name</h5>
                                <h6>{currentBackup.name.length}/60</h6>
                            </div>
                            <input
                                className="userfulInputField"
                                placeholder="Enter name here..."
                                type="text"
                                maxLength={60}
                                value={currentBackup.name}
                                onChange={handleNameChange}
                            />
                            {nameError && registerChange && <div className="errorText">{nameError}</div>}
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">Include Media</h4>
                            <h5 className="gcm-form-ContentsDescription">
                                Should this backup include media files, this will make the size of the backup file
                                substantially larger.
                            </h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="compositeContainer">
                                <div
                                    className={`fieldRowWrapper `}
                                    onClick={(e) => {
                                        setCurrentBackup({
                                            ...currentBackup,
                                            supports: {
                                                ...currentBackup.supports,
                                                supportsMedia: !currentBackup.supports.supportsMedia,
                                            },
                                        });
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div
                                        className={`checkbox ${currentBackup.supports.supportsMedia ? "checked" : null} `}
                                    >
                                        {currentBackup.supports.supportsMedia ? (
                                            <FontAwesomeIcon icon={faCheck} />
                                        ) : null}
                                    </div>
                                    <div className="fieldColumnWrapper shortGap">
                                        <div className="gcm-mainText">Media Files</div>
                                    </div>
                                </div>
                                {typeError && registerChange && <div className="errorText">{typeError}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">System</h4>
                            <h5 className="gcm-form-ContentsDescription">
                                Select which system you would like to create a backup of.
                            </h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Cluster</h5>
                            </div>
                            <ClustersDropdownSelector clusterID={currentBackup.id} handleClick={handleClusterClick} />
                            {clusterError && registerChange && <div className="errorText">{clusterError}</div>}
                        </div>
                    </div>
                </div>
                <div className="gcm-form-Footer">
                    <button className="secondaryUserfulButton" onClick={onFormHide}>
                        Cancel
                    </button>
                    <button className="primaryUserfulButton" onClick={onCreate} disabled={!canCreate}>
                        {false ? "Save Changes" : "Create"}
                    </button>
                </div>
            </Offcanvas>

            <Modal
                centered
                show={showConfirmation}
                onHide={() => {
                    setShowConfirmation(false);
                }}
            >
                <div className="gcm-pop-up-modal-wrapper">
                    <div className="gcm-pop-up-modal-wrapper-body">
                        <div className="gcm-pop-up-modal-wrapper-head">
                            <div className="gcm-pop-up-modal-wrapper-title">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <h1>Are you sure you want to leave?</h1>
                            </div>
                            <Button
                                variant="secondary"
                                iconOnly
                                onClick={() => {
                                    setShowConfirmation(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faMultiply} />
                            </Button>
                        </div>
                        <p>
                            Are you sure you want to leave without saving your changes? Any unsaved modifications will
                            be lost.
                        </p>
                    </div>
                    <div className="confirmationDialogHorizontalWrapper reversed">
                        <Button
                            variant="danger"
                            onClick={(e) => {
                                setShowForm(false);
                                setShowConfirmation(false);
                                onRestore();
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={(e) => {
                                setShowConfirmation(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
